import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { TicketSchema, TicketCreateInputSchema, TicketUpdateInputSchema, type Ticket, type TicketCreateInput, type TicketUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `ticket` (demande entrante prise en charge, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`TicketCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide. Les champs standard
 * sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { TicketCreateInput };

/** Nom de la collection Firestore de la famille ticket. */
export const TICKETS_COLLECTION = 'tickets';

/**
 * Repository typé pour la famille `ticket`.
 * Toute lecture est validée par `TicketSchema`, toute écriture par
 * `TicketCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface TicketRepository {
  create(input: TicketCreateInput): Promise<Ticket>;
  update(id: string, patch: TicketUpdateInput): Promise<Ticket>;
  list(options?: ListOptions): Promise<Ticket[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Ticket | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Ticket>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Ticket>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Ticket>;
}

const ticketConverter = createConverter<Ticket>((raw: DocumentData) => TicketSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createTicketRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): TicketRepository {
  const col = collection(db, TICKETS_COLLECTION).withConverter(ticketConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Ticket>(db, col, TICKETS_COLLECTION, getActorId);

  return {
    async create(input: TicketCreateInput): Promise<Ticket> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = TicketCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.priority !== undefined) optional['priority'] = fields.priority;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.assignedWorkerId !== undefined) optional['assignedWorkerId'] = fields.assignedWorkerId;
      if (fields.slaDueAt !== undefined) optional['slaDueAt'] = fields.slaDueAt;
      if (fields.channel !== undefined) optional['channel'] = fields.channel;
      if (fields.resolutionSummary !== undefined) optional['resolutionSummary'] = fields.resolutionSummary;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Ticket);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return TicketSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: TicketUpdateInput): Promise<Ticket> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = TicketUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, TICKETS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`ticket ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Ticket[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Ticket | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
